import { LiveSttFragment } from '@ftr/contracts/regional-api'
import { assert } from '@ftr/contracts/shared/Assertions'
import { generateUuid, LocalDateTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { DefaultTimeStringParseMethod, isNotNullOrUndefined, TimeStringParser } from '@ftr/foundation'
import { SttEdit } from '@ftr/regional-api-contracts'
import { LocalDateTime, LocalTime } from '@js-joda/core'
import { match } from 'ts-pattern'
import { RealTimeSttRemark, RealTimeSttSegmentMap } from '../../../types'
import { remarkFragmentToSttEditFragment } from '../../../utils'
import { STT_FRAGMENT_SEPARATOR } from '../edit-operations'
import { OperationField, OperationResult, ValidationErrorCode } from './operation-result'
import { SttEditOperationSideEffect } from './operation-side-effect'
import { bumpStartTimeEditsToProduceUniqueTime } from './unique-start-time-utils'
import { boundaryErrorToValidationResult, checkEditForBoundaryErrors } from './within-boundaries-utils'

/**
 * Requires at least one of startTimeInput or endTimeInput to be provided
 */
export interface EditRemarkBoundingTimesOperation {
  type: 'editRemarkBoundingTimes'
  remarkId: Uuid
  // Undefined if it has not been edited and should instead be looked up in the segment map (preserves precision)
  startTime: string | undefined
  // Undefined if it has not been edited and should instead be looked up in the segment map (preserves precision)
  endTime: string | undefined
}

export function editRemarkBoundingTimesTransaction(
  operation: EditRemarkBoundingTimesOperation,
  segmentMap: RealTimeSttSegmentMap,
): OperationResult {
  // Find the remark in the segment map
  const remark = segmentMap.findRemark(operation.remarkId)
  if (!remark) {
    throw new Error(`Remark with ID ${operation.remarkId} not found in segment map`)
  }

  assert(
    operation.startTime !== undefined || operation.endTime !== undefined,
    'Start time or end time must be defined for bounding times operation',
  )
  const newStartTime =
    operation.startTime !== undefined ? safeParseTime(operation.startTime) : remark.startTime.toLocalTime()
  const newEndTime = operation.endTime !== undefined ? safeParseTime(operation.endTime) : remark.endTime.toLocalTime()
  if (!newStartTime || !newEndTime) {
    const invalidFields: OperationField[] = [
      ...(newStartTime ? [] : (['startTime'] as const)),
      ...(newEndTime ? [] : (['endTime'] as const)),
    ]
    const errorCode = match({ newStartTime, newEndTime })
      .returnType<ValidationErrorCode>()
      .with({ newStartTime: undefined, newEndTime: undefined }, () => 'invalid-start-end-time')
      .with({ newStartTime: undefined }, () => 'invalid-start-time')
      .with({ newEndTime: undefined }, () => 'invalid-end-time')
      .run()

    return {
      type: 'validationError',
      errorCode,
      invalidFields,
    }
  }

  const operationStartTime = remark.startTime.toLocalDate().atTime(newStartTime)
  const operationEndTime = remark.endTime.toLocalDate().atTime(newEndTime)

  if (operationStartTime.isAfter(operationEndTime)) {
    return {
      type: 'validationError',
      errorCode: 'end-time-before-start-time',
      invalidFields: ['startTime', 'endTime'],
    }
  }

  const segmentForNewTime = segmentMap.getSegmentAt(operationStartTime)
  assert(segmentForNewTime)

  const editsToEnsureUniqueStartTime = bumpStartTimeEditsToProduceUniqueTime(
    operationStartTime,
    segmentMap.withDeletedRemark(remark.id),
    'forward',
  )
  const editsToBoundRemark = computeEditsToBoundRemark(
    new LocalDateTimeRange(operationStartTime, operationEndTime),
    remark,
  )
  const boundaryErrors = editsToBoundRemark
    .map(edit => checkEditForBoundaryErrors(edit, segmentMap))
    .filter(isNotNullOrUndefined)
  if (boundaryErrors.length) {
    return boundaryErrorToValidationResult(boundaryErrors[0])
  }

  const sideEffects: SttEditOperationSideEffect[] = []

  if (operation.startTime) {
    // show a 'stt moved' toast when the start time is edited
    // we say 'moved' for consistency, but accept the position may not change for small edits
    // this is the preferred UX outlined by design/product
    sideEffects.push({
      type: 'notifyRemarkMoved',
      remarkId: remark.id,
      segmentId: segmentForNewTime.id,
    })
  }

  return {
    type: 'transaction',
    transaction: {
      id: generateUuid(),
      edits: [...editsToEnsureUniqueStartTime, ...editsToBoundRemark],
    },
    sideEffects,
  }
}

function computeEditsToBoundRemark(boundingTimeRange: LocalDateTimeRange, remark: RealTimeSttRemark): SttEdit[] {
  assert(remark.fragments, 'Remarks require fragments for editing.')
  // For an empty utterance we need to delete and soft create it again
  if (remark.fragments.length === 0) {
    return [
      {
        type: 'deleteUtterance',
        utteranceId: remark.id,
      },
      {
        type: 'softCreateUtterance',
        startTime: boundingTimeRange.start,
        endTime: boundingTimeRange.end,
        utteranceId: remark.id,
        dialect: remark.dialect ?? null,
        speakerId: remark.speakerId,
      },
    ]
  }

  const localDateTimeFragments = remark.fragments.map(fragment =>
    remarkFragmentToSttEditFragment(fragment, remark.startTime),
  )

  const boundedFragments = localDateTimeFragments.map((fragment, index, fragments) => ({
    ...fragment,
    // Always update first fragment start and last fragments end times
    startTime: index === 0 ? boundingTimeRange.start : boundByTimeRange(fragment.startTime, boundingTimeRange),
    endTime:
      index === fragments.length - 1 ? boundingTimeRange.end : boundByTimeRange(fragment.endTime, boundingTimeRange),
  }))
  // Fragments should not share the same start time otherwise the content will not be display in the correct order
  const updatedFragments = mergeFragmentsToEnsureUniqueStartTimes(boundedFragments)

  return [
    {
      type: 'upsertUtterance',
      utteranceId: remark.id,
      fragments: updatedFragments,
      dialect: remark.dialect ?? null,
      speakerId: remark.speakerId,
    },
  ]
}

function mergeFragmentsToEnsureUniqueStartTimes(fragments: LiveSttFragment[]): LiveSttFragment[] {
  const mergedFragments: LiveSttFragment[] = []
  for (const fragment of fragments) {
    const lastFragment = mergedFragments.at(-1)
    const matchingStartTimes = lastFragment && lastFragment.startTime.equals(fragment.startTime)
    const consecutive = lastFragment && lastFragment.endTime.equals(fragment.startTime)
    const fragmentZeroDuration = fragment.startTime.equals(fragment.endTime)
    const mergeWithLast = matchingStartTimes || (consecutive && fragmentZeroDuration)
    if (!mergeWithLast) {
      mergedFragments.push(fragment)
      continue
    }

    mergedFragments[mergedFragments.length - 1] = {
      startTime: lastFragment.startTime,
      endTime: fragment.endTime,
      content: lastFragment.content + STT_FRAGMENT_SEPARATOR + fragment.content,
    }
  }
  return mergedFragments
}

function boundByTimeRange(original: LocalDateTime, boundingTimeRange: LocalDateTimeRange): LocalDateTime {
  if (original.isBefore(boundingTimeRange.start)) {
    return boundingTimeRange.start
  }
  if (original.isAfter(boundingTimeRange.end)) {
    return boundingTimeRange.end
  }
  return original
}

function safeParseTime(value: string): LocalTime | undefined {
  try {
    return TimeStringParser.parse(value, [DefaultTimeStringParseMethod])
  } catch {
    return undefined
  }
}
